/**
 * Spring loaders (pushers): depth a shelf reserves behind the product for the
 * paddle and the compressed spring (DEV-3518).
 *
 * The PSA format has no loader field. Planners record a pusher as a NEGATIVE
 * Back overhang on the shelf's fixture record (confirmed by the planner,
 * 2026-10-01): the back |overhang| of the shelf cannot be merchandised. That
 * is the only encoding read or written. Desc 5 and Value 4 mean nothing here
 * (an earlier draft of ours also used them; no planner does).
 *
 * On the pog the loader is `PlanogramShelf.loaderDepth`, in the pog's x100
 * units like `depth`. The shelf's `depth` stays physical; the stock sits at the
 * front and the loader behind it.
 */

const toFiniteNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

/**
 * A length in file units as pog hundredths, rounded UP, so the depth left for
 * stock never overstates what fits. The epsilon absorbs float noise
 * (3.81 * 100 = 380.99999999999994).
 */
const toHundredthsRoundedUp = (value: number): number => Math.ceil(value * 100 - 1e-6);

/** A stored loader depth (x100) as a whole number of hundredths, rounded up; 0 for none. */
export const normalizeLoaderDepth = (loaderDepth: unknown): number => {
  const depth = toFiniteNumber(loaderDepth);
  return depth > 0 ? Math.ceil(depth - 1e-9) : 0;
};

/**
 * A PSA fixture's loader depth in pog hundredths, or null when the fixture
 * records none: |Back overhang| when the overhang is negative. A positive
 * overhang is space PAST the back of the shelf, not a loader. Used for the
 * pog shelf on PSA import and for `master_fixture.loader_depth` on a PSA
 * fixture upload, so the two agree.
 */
export const shelfLoaderDepthFromPsaFixture = (fields: {
  back_overhang?: unknown;
}): number | null => {
  const backOverhang = toFiniteNumber(fields.back_overhang);
  return backOverhang < 0 ? toHundredthsRoundedUp(-backOverhang) : null;
};

/** The fixture record field a loader shelf exports with (raw PSA units), or null for none. */
export const psaFixtureLoaderFields = (
  loaderDepth: number | null | undefined,
): { backOverhang: number } | null => {
  const depth = normalizeLoaderDepth(loaderDepth);
  if (depth <= 0) return null;
  return { backOverhang: -depth / 100 };
};

/** True when the shelf reserves any depth for a loader. */
export const hasShelfLoader = (shelf: { loaderDepth?: unknown }) =>
  normalizeLoaderDepth(shelf.loaderDepth) > 0;

/**
 * The depth stock may use: physical depth minus the loader, never below 0.
 * The ONE place this subtraction is written; every pogger request builder and
 * the pog's own units-deep fallback go through it. A shelf without a loader
 * gets its `depth` back untouched (same value, same type), so requests for
 * loader-free stores stay byte-identical.
 */
export const usableShelfDepth = (shelf: { depth: number; loaderDepth?: unknown }): number => {
  const loaderDepth = normalizeLoaderDepth(shelf.loaderDepth);
  if (loaderDepth <= 0) return shelf.depth;
  return Math.max(0, shelf.depth - loaderDepth);
};

/**
 * Units deep the way the pogger counts them (`Candidate.facings_deep`):
 * `max(0, floor((depth - nesting) / (unit depth - nesting)))`. OWA's requests
 * send no nesting, so callers matching a solve pass 0.
 */
export const unitsDeepOnDepth = ({
  depth,
  unitDepth,
  nestingDepth = 0,
}: {
  depth: number;
  unitDepth: number;
  nestingDepth?: number;
}): number => {
  const step = unitDepth - nestingDepth;
  if (!(unitDepth > 0) || !(step > 0)) return 0;
  return Math.max(0, Math.floor((depth - nestingDepth) / step));
};

/**
 * Stores the units deep a loader leaves on every item of a loader shelf that
 * has none stated yet, computed as the pogger does from the usable depth (the
 * request sent `usableShelfDepth`). Items that already carry a count (the
 * pogger's `units_deep`, a PSA's DFacings) keep it. Loader-free shelves are
 * not touched, so their items keep the geometric fallback exactly as before.
 * Returns how many items were stamped.
 */
export const stampLoaderUnitsDeep = (pog: {
  planogram: {
    bays: {
      shelves: {
        depth: number;
        loaderDepth?: unknown;
        maxFrontOverhang?: number;
        items: { unitsDeep: number | null; getDepth: () => number }[];
      }[];
    }[];
  };
}): number => {
  let stamped = 0;
  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      if (!hasShelfLoader(shelf)) continue;
      const depth = usableShelfDepth(shelf);
      for (const item of shelf.items) {
        if (item.unitsDeep != null) continue;
        // Oversized packs must remain dependent on the shelf permission.
        if (shelf.maxFrontOverhang && item.getDepth() > depth) continue;
        item.unitsDeep = unitsDeepOnDepth({ depth, unitDepth: item.getDepth() });
        stamped += 1;
      }
    }
  }
  return stamped;
};
